import { authStore } from "@/lib/authStore";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders(extra = {}) {
  const token = authStore.getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/**
 * fetch wrapper with single-attempt silent-refresh on 401.
 *
 * - Hardened mode (csrf_token present, refresh cookie set): on 401 we call
 *   /auth/refresh exactly once via authStore.refreshSilently() (single-flight),
 *   then retry the original request with the new access token.
 * - Legacy mode (localStorage token only): no refresh possible — caller sees
 *   the 401 and handles re-auth.
 *
 * Always sends `` so the httpOnly refresh cookie and
 * readable csrf cookie are transmitted on every call.
 */
export async function apiFetch(path, init = {}) {
  const url = path.startsWith("http") ? path : `${API_URL}${path}`;
  const headers = authHeaders(init.headers || {});
  const opts = { ...init, headers };
  let res = await fetch(url, opts);
  if (res.status !== 401) return res;
  // Try a silent refresh exactly once (only possible in hardened mode).
  if (!authStore.isHardened()) return res;
  const ok = await authStore.refreshSilently();
  if (!ok) return res;
  const retried = await fetch(url, { ...opts, headers: authHeaders(init.headers || {}) });
  return retried;
}

async function jsonOrThrow(res, fallbackMsg) {
  if (!res.ok) {
    let detail;
    try { detail = (await res.json())?.detail; } catch (_) { /* noop */ }
    throw new Error(detail || fallbackMsg);
  }
  return res.json();
}

export const api = {
  async getConversations() {
    return jsonOrThrow(await apiFetch("/conversations"), "Failed to fetch conversations");
  },
  async createConversation(title = "New Chat") {
    return jsonOrThrow(
      await apiFetch("/conversations", {
        method: "POST",
        body: JSON.stringify({ title }),
      }),
      "Failed to create conversation"
    );
  },
  async deleteConversation(id) {
    return jsonOrThrow(
      await apiFetch(`/conversations/${id}`, { method: "DELETE" }),
      "Failed to delete conversation"
    );
  },
  async updateConversationTitle(id, title) {
    return jsonOrThrow(
      await apiFetch(`/conversations/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }),
      "Failed to update conversation"
    );
  },
  async getMessages(conversationId) {
    return jsonOrThrow(
      await apiFetch(`/conversations/${conversationId}/messages`),
      "Failed to fetch messages"
    );
  },
  async sendMessage(conversationId, content, attachmentIds) {
    const res = await apiFetch(`/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content, attachment_ids: attachmentIds || [] }),
    });
    if (!res.ok) {
      let detail;
      try { detail = (await res.json())?.detail; } catch (_) { /* noop */ }
      throw new Error(detail || "Failed to send message");
    }
    return res;
  },
  async uploadChatAttachment(file) {
    // FormData upload — must NOT set Content-Type (browser adds the boundary)
    const fd = new FormData();
    fd.append("file", file);
    const token = authStore.getAccessToken();
    const res = await fetch(`${API_URL}/chat/attachments`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    return jsonOrThrow(res, "Failed to upload attachment");
  },
  async getPlans() {
    return jsonOrThrow(await apiFetch("/plans"), "Failed to fetch plans");
  },
  async createOrder(planId) {
    return jsonOrThrow(
      await apiFetch("/subscriptions/create-order", {
        method: "POST",
        body: JSON.stringify({ plan_id: planId }),
      }),
      "Failed to create order"
    );
  },
  async verifyPayment(data) {
    return jsonOrThrow(
      await apiFetch("/subscriptions/verify-payment", {
        method: "POST",
        body: JSON.stringify(data),
      }),
      "Failed to verify payment"
    );
  },
  async cancelSubscription() {
    return jsonOrThrow(
      await apiFetch("/subscriptions/cancel", { method: "POST" }),
      "Failed to cancel subscription"
    );
  },
  async getBillingHistory() {
    return jsonOrThrow(await apiFetch("/billing/history"), "Failed to fetch billing history");
  },
  async exportDocument(content, title, format) {
    const res = await apiFetch(`/export/${format}`, {
      method: "POST",
      body: JSON.stringify({ content, title, format }),
    });
    if (!res.ok) throw new Error("Failed to export document");
    return res.blob();
  },
  async getUsage() {
    return jsonOrThrow(await apiFetch("/usage/me"), "Failed to fetch usage");
  },
  async estimateCost(content) {
    const res = await apiFetch("/estimate-cost", {
      method: "POST",
      body: JSON.stringify({ content }),
    });
    if (!res.ok) return null;
    return res.json();
  },
  async logout() {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch (_) { /* logout is best-effort */ }
    authStore.clear();
  },
};

export default api;
